import { MetalsProcessStation, MetalsTask, MetalsTaskActivationCondition, MetalsTaskStatus } from "@prism/prism-shared";

/**
 * Determines which waiting tasks should be activated based on their predecessor tasks' completion status.
 * 
 * @param allProjectTasks - All tasks in the project(s)
 * @param updatedTasksMap - Map of tasks that have been/will be updated (taskId -> task)
 * @returns Array of tasks that should be activated
 */
export function determineTaskActivations(
  allProjectTasks: MetalsTask[],
  updatedTasksMap: Map<string, MetalsTask>,
  processStations: MetalsProcessStation[] = [],
): MetalsTask[] {
  const tasksToActivate: MetalsTask[] = [];
  const taskIdToTask = new Map<string, MetalsTask>(allProjectTasks.map(task => [task.id, task]));
  const transparentTaskIds = new Set(
    processStations
      .filter(station => station.transparentForNextTaskActivation ?? false)
      .flatMap(station => station.taskIds ?? [])
  );

  // Helper function to get the current state of a task
  const getTaskState = (taskId: string): MetalsTask | undefined => {
    return updatedTasksMap.get(taskId) || taskIdToTask.get(taskId);
  };

  const getPredecessorTasks = (taskId: string): MetalsTask[] => {
    return allProjectTasks.filter(t => t.nextTaskId === taskId);
  };

  const getEffectivePredecessorTasks = (task: MetalsTask, visitedTaskIds = new Set<string>()): MetalsTask[] => {
    if (visitedTaskIds.has(task.id)) {
      return [];
    }
    visitedTaskIds.add(task.id);

    const predecessorTasks = getPredecessorTasks(task.id);

    return predecessorTasks.flatMap(predecessorTask => {
      if (!transparentTaskIds.has(predecessorTask.id)) {
        return [predecessorTask];
      }

      const upstreamPredecessorTasks = getEffectivePredecessorTasks(predecessorTask, new Set(visitedTaskIds));

      // If a transparent task is the root, fall back to requiring that task so downstream
      // work does not activate with no real upstream completion signal.
      return upstreamPredecessorTasks.length ? upstreamPredecessorTasks : [predecessorTask];
    });
  };

  // Check each task in the project for potential activation
  allProjectTasks.forEach(task => {
    // Skip if already being updated or not in waiting status
    if (updatedTasksMap.has(task.id) || task.status !== MetalsTaskStatus.Waiting) {
      return;
    }

    const predecessorTasks = getEffectivePredecessorTasks(task);

    if (predecessorTasks.length === 0) {
      return; // No predecessors, skip
    }

    let shouldActivate = false;

    // Check activation condition
    if (task.activationCondition === MetalsTaskActivationCondition.SomeQuantityCompleted) {
      // ALL predecessors must have at least completedQuantity > 0
      shouldActivate = predecessorTasks.every(predTask => {
        const currentState = getTaskState(predTask.id);
        return currentState && currentState.completedQuantity > 0;
      });
    } else if (task.activationCondition === MetalsTaskActivationCondition.AllQuantityCompleted) {
      // All predecessors must have completedQuantity >= totalQuantity
      shouldActivate = predecessorTasks.every(predTask => {
        const currentState = getTaskState(predTask.id);
        return currentState && currentState.completedQuantity >= currentState.totalQuantity;
      });
    }

    if (shouldActivate) {
      tasksToActivate.push({
        ...task,
        status: MetalsTaskStatus.Active
      });
    }
  });

  return tasksToActivate;
}
